import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getPitch, loadBackgrounds } from './services/pitchService';
import SlideRenderer from './slides/SlideRenderer';
import { useAltLogo } from '../../hooks/useQRLogo';
import { colors } from '../../theme/tikabTokens';

export default function AudienceView() {
  const { pitchId }    = useParams();
  const [pitch,        setPitch]       = useState(null);
  const [backgrounds,  setBackgrounds] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading,      setLoading]     = useState(true);
  const containerRef   = useRef(null);
  const altLogoUrl     = useAltLogo();

  // ── Load pitch data and backgrounds once ───────────────────────────────────
  useEffect(() => {
    (async () => {
      const [p, bgs] = await Promise.all([getPitch(pitchId), loadBackgrounds()]);
      if (p) {
        setPitch(p);
        setCurrentSlide(p.currentSlide ?? 0);
      }
      setBackgrounds(bgs);
      setLoading(false);
    })();
  }, [pitchId]);

  // ── Firestore real-time sync for currentSlide ──────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'pitches', pitchId), (snap) => {
      if (snap.exists()) {
        const idx = snap.data().currentSlide ?? 0;
        setCurrentSlide(idx);
      }
    });
    return unsub;
  }, [pitchId]);

  // ── Auto fullscreen on load ────────────────────────────────────────────────
  useEffect(() => {
    if (!loading && containerRef.current) {
      containerRef.current.requestFullscreen?.().catch(() => {});
    }
  }, [loading]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', bgcolor: '#000' }}>
        <CircularProgress sx={{ color: colors.orange }} />
      </Box>
    );
  }

  if (!pitch) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', bgcolor: '#000' }}>
        <Typography color="error">Pitch hittades inte</Typography>
      </Box>
    );
  }

  const slide = pitch.slides?.[currentSlide] ?? null;

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100vw', height: '100vh',
        bgcolor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {slide ? (
        <Box sx={{ width: '100%', height: '100%' }}>
          <SlideRenderer slide={slide} backgrounds={backgrounds} slideTheme={pitch?.slideTheme} altLogoUrl={altLogoUrl}
            qrUrl={pitch?.slideTheme?.showQr && pitch?.shareToken ? `${window.location.origin}/p/${pitch.shareToken}` : undefined}
            qrLogoUrl={pitch?.qrLogoUrl || undefined} />
        </Box>
      ) : (
        <Typography sx={{ color: '#333', fontSize: '1.5rem' }}>
          Inga slides
        </Typography>
      )}
    </Box>
  );
}
